import { Divider, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import LoaderBox from "@entrilia/shared/components/LoaderBox";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import {
  selectChartOfAccounts,
  selectMeasures,
  selectMeasuresStructure,
} from "../../../../store/selectors/metaDataSelectors";
import SplitPane from "../../../common/split-pane/SplitPane";
import ExpandableDialog from "../../../ExpandableDialog";
import FormulaContainer from "../customMeasure/FormulaContainer";
import { useCustomMeasure } from "../customMeasure/hooks/useCustomMeasure";
import { ChartOfAccountsContextProvider } from "./contexts/ChartOfAccountsContextProvider";
import LedgerAccountsWrapper from "./LedgerAccountsWrapper";
import MeasureSelector from "./MeasureSelector";
import { Properties } from "./properties/Properties";
import type { MeasureDescriptor } from "../../../../../../../../api/types/svTypes.ts";

interface ChartOfAccountsProps {
  initialMeasure: MeasureDescriptor;
  onClose: () => void;
}
export default function ChartOfAccounts({ initialMeasure, onClose }: ChartOfAccountsProps) {
  const storedMeasures = useSelector(selectMeasures);
  const measuresStructure = useSelector(selectMeasuresStructure);
  const chartOfAccounts = useSelector(selectChartOfAccounts);
  const [selectedMeasure, setSelectedMeasure] = useState<MeasureDescriptor>(initialMeasure);
  const state = useCustomMeasure({ measure: selectedMeasure });

  const onMeasureSelect = useCallback(
    (measure: MeasureDescriptor) => {
      setSelectedMeasure(measure);
    },
    [setSelectedMeasure]
  );

  return (
    <ExpandableDialog title={<Typography variant="h6">Chart of Accounts</Typography>} separateTitle onClose={onClose}>
      {state.dataLoadingError ? (
        <DataLoadingFailed text={state.dataLoadingError} />
      ) : (
        <LoaderBox loading={state.loading}>
          <Grid container sx={{ flex: 1, flexWrap: "nowrap", position: "relative", width: "100%" }}>
            <ChartOfAccountsContextProvider>
              <SplitPane
                initialPrimarySize="30%"
                minPrimarySize="350px"
                minSecondarySize="350px"
                primary={
                  <Stack
                    spacing={2}
                    useFlexGap
                    sx={{
                      maxHeight: "100%",
                      width: "100%",
                      pt: 2.5,
                      pb: 1.25,
                      pl: 3,
                      pr: 1.5,
                    }}
                  >
                    <MeasureSelector
                      measures={storedMeasures}
                      measuresStructure={measuresStructure}
                      measure={selectedMeasure}
                      onSelect={onMeasureSelect}
                    />
                    <FormulaContainer state={state} measure={selectedMeasure} editable={false} hideCheck />
                    <Divider sx={{ mr: -1 }} />
                    <ScrollableFlexContainer sx={{ "& .MuiBox-root": { overflowX: "hidden" } }}>
                      <Properties state={state} />
                    </ScrollableFlexContainer>
                  </Stack>
                }
                secondary={<LedgerAccountsWrapper chartOfAccounts={chartOfAccounts} measure={selectedMeasure} />}
              />
            </ChartOfAccountsContextProvider>
          </Grid>
        </LoaderBox>
      )}
    </ExpandableDialog>
  );
}
